import { RatioIcon, SquareIcon, SquareSquare } from "lucide-react"

import { UNIT_OPTIONS } from "../../utils/predefined_sizes"

const parseSizeToTailwind = ({ size, type }) => {
  if (!size || !size.value) return ''

  const { value, unit, predefined } = size
  const prefix = PREFIXES.size[type]

  return predefined ? `${prefix}${value}` : `${prefix}[${value}${unit}]`
}

const mergeTailwindSpacing = ({ type, parsed }) => {
  if (!parsed.length) {
    return ''
  }

  const prefix = PREFIXES.space[type]
  const dirs = [
    { key: 't', match: /^(mt-|pt-|t-)/ },
    { key: 'b', match: /^(mb-|pb-|b-)/ },
    { key: 'l', match: /^(ml-|pl-|l-)/ },
    { key: 'r', match: /^(mr-|pr-|r-)/ },
  ]

  const values = Object.fromEntries(
    dirs.map(({ key, match }) => [key, parsed.find(cls => match.test(cls))]),
  )

  const getValue = cls => {
    if (!cls) return { value: undefined, isArbitrary: false }
    const match = cls.match(/-\[([\w.%]+)\]$/)
    if (match) return { value: match[1], isArbitrary: true }
    const match2 = cls.match(/-([\w.%]+)$/)
    if (match2) return { value: match2[1], isArbitrary: false }
    return { value: undefined, isArbitrary: false }
  }

  const tVal = getValue(values.t)
  const bVal = getValue(values.b)
  const lVal = getValue(values.l)
  const rVal = getValue(values.r)

  const allPresent = values.t && values.b && values.l && values.r
  const allEqual = tVal.value === bVal.value && tVal.value === lVal.value && tVal.value === rVal.value
  const allSameArbitrary = tVal.isArbitrary === bVal.isArbitrary && tVal.isArbitrary === lVal.isArbitrary && tVal.isArbitrary === rVal.isArbitrary

  const result = []
  if (allPresent && allEqual && allSameArbitrary) {
    result.push(`${prefix}-${tVal.isArbitrary ? `[${tVal.value}]` : tVal.value}`)
  } else {
    if (values.t && values.b && tVal.value === bVal.value && tVal.isArbitrary === bVal.isArbitrary) {
      result.push(`${prefix}y-${tVal.isArbitrary ? `[${tVal.value}]` : tVal.value}`)
    } else {
      if (values.t) result.push(values.t)
      if (values.b) result.push(values.b)
    }

    if (values.l && values.r && lVal.value === rVal.value && lVal.isArbitrary === rVal.isArbitrary) {
      result.push(`${prefix}x-${lVal.isArbitrary ? `[${lVal.value}]` : lVal.value}`)
    } else {
      if (values.l) result.push(values.l)
      if (values.r) result.push(values.r)
    }
  }

  return result.join(' ')
}

const parseSpaceToTailwind = ({ spaces, type }) => {
  if (!spaces) {
    return ''
  }

  const positions = ['top', 'bottom', 'left', 'right']
  const parsed = positions.map(position => {
    const space = spaces[position]
    if (!space || !space.value) {
      return ''
    }

    const { value, unit, predefined } = space
    const baseClass = `${PREFIXES.space[type]}${PREFIXES.position[position]}`
    return predefined ? `${baseClass}-${value}` : `${baseClass}-[${value}${unit}]`
  }).filter(Boolean)

  return mergeTailwindSpacing({ type, parsed })
}

const parseTailwindToSize = (className, type) => {
  if (!className) return { value: null, unit: null, predefined: true }

  const value = className.replace(PREFIXES.size[type], '')
  if (!value.includes('[')) return { value, unit: null, predefined: true }

  const units = UNIT_OPTIONS.map(u => u.value).join('|')
  const match = value.match(new RegExp(`\\[(.*?)(${units})\\]`))
  if (match) return { value: match[1], unit: match[2], predefined: false }
  return { value: null, unit: null, predefined: true }
}

function extractSpaceValue(className, prefix) {
  if (!className || !className.startsWith(prefix)) return null
  const value = className.replace(prefix, '')
  if (!value.includes('[')) return { value, unit: null, predefined: true }
  const units = UNIT_OPTIONS.map(u => u.value).join('|')
  const match = value.match(new RegExp(`\\[(.*?)(${units})\\]`))
  if (match) return { value: match[1], unit: match[2], predefined: false }
  return { value: null, unit: null, predefined: true }
}

const parseSpaceString = (spaceString = '', type) => {
  const classes = spaceString.split(' ').filter(Boolean)
  const getClass = prefix => classes.find(cls => cls.startsWith(prefix))

  const allClass = getClass(`${PREFIXES.space[type]}-`)
  const yClass = getClass(`${PREFIXES.space[type]}y-`)
  const xClass = getClass(`${PREFIXES.space[type]}x-`)
  const sideClasses = {
    top: getClass(`${PREFIXES.space[type]}${PREFIXES.position.top}-`),
    bottom: getClass(`${PREFIXES.space[type]}${PREFIXES.position.bottom}-`),
    left: getClass(`${PREFIXES.space[type]}${PREFIXES.position.left}-`),
    right: getClass(`${PREFIXES.space[type]}${PREFIXES.position.right}-`),
  }

  const getClassForPosition = position => {
    if (sideClasses[position]) return sideClasses[position]
    if ((position === 'top' || position === 'bottom') && yClass) return yClass
    if ((position === 'left' || position === 'right') && xClass) return xClass
    if (allClass) return allClass
    return ''
  }

  const parsedSpaces = {
    top: parseTailwindToSpace(getClassForPosition('top'), type, 'top'),
    bottom: parseTailwindToSpace(getClassForPosition('bottom'), type, 'bottom'),
    left: parseTailwindToSpace(getClassForPosition('left'), type, 'left'),
    right: parseTailwindToSpace(getClassForPosition('right'), type, 'right'),
  }

  return {
    value: parsedSpaces,
    type: getSpaceType(parsedSpaces),
  }
}

const getSpaceType = parsedSpaces => {
  const getValue = space => space.value || null
  const topValue = getValue(parsedSpaces.top)
  const bottomValue = getValue(parsedSpaces.bottom)
  const leftValue = getValue(parsedSpaces.left)
  const rightValue = getValue(parsedSpaces.right)

  const allEmpty = !topValue && !bottomValue && !leftValue && !rightValue
  const allSame = topValue === bottomValue && topValue === leftValue && topValue === rightValue
  const axisEqual = topValue === bottomValue || leftValue === rightValue

  let spaceType = 'individual'
  if (allEmpty || allSame) {
    spaceType = 'all'
  } else if (axisEqual) {
    spaceType = 'axis'
  }

  return spaceType
}

const parseTailwindToSpace = (className, type, position) => {
  if (!className) {
    return { value: null, unit: null, predefined: true }
  }

  const sidePrefix = `${PREFIXES.space[type]}${PREFIXES.position[position]}-`
  const sideResult = extractSpaceValue(className, sidePrefix)
  if (sideResult) {
    return sideResult
  }

  if (position === 'top' || position === 'bottom') {
    const yPrefix = `${PREFIXES.space[type]}y-`
    const yResult = extractSpaceValue(className, yPrefix)
    if (yResult) {
      return yResult
    }
  }

  if (position === 'left' || position === 'right') {
    const xPrefix = `${PREFIXES.space[type]}x-`
    const xResult = extractSpaceValue(className, xPrefix)
    if (xResult) {
      return xResult
    }
  }

  const allPrefix = `${PREFIXES.space[type]}-`
  const allResult = extractSpaceValue(className, allPrefix)
  if (allResult) {
    return allResult
  }

  return { value: null, unit: null, predefined: true }
}

const PREDEFINED_OPTIONS_TOGGLE = [
  { label: 'Auto', value: 'auto' },
  { label: '1', value: '1' },
  { label: '3', value: '3' },
  { label: '5', value: '5' },
  { label: '10', value: '10' },
  { label: '15', value: '15' },
]

const CUSTOM_OPTIONS_TOGGLE = [
  { label: '0', value: '0' },
  { label: '3', value: '3' },
  { label: '5', value: '5' },
  { label: '10', value: '10' },
  { label: '15', value: '15' },
  { label: '20', value: '20' },
]

const PREDEFINED_SIZES = {
  width: [
    { label: 'Auto', value: 'auto' },
    { label: 'Full', value: 'full' },
    { label: 'Screen', value: 'screen' },
    { label: 'Small', value: 'sm' },
    { label: 'Medium', value: 'md' },
    { label: 'Large', value: 'lg' },
    { label: '3', value: '3' },
    { label: '5', value: '5' },
    { label: '10', value: '10' },
  ],
  height: [
    { label: 'Auto', value: 'auto' },
    { label: 'Full', value: 'full' },
    { label: 'Screen', value: 'screen' },
    { label: '3', value: '3' },
    { label: '5', value: '5' },
    { label: '10', value: '10' },
  ],
}

const PREFIXES = {
  size: { width: 'w-', height: 'h-' },
  space: { margin: 'm', padding: 'p' },
  position: { top: 't', bottom: 'b', left: 'l', right: 'r' },
}

const SPACING_OPTIONS = [
  {
    tooltip: 'Same for all',
    value: 'all',
    icon: <SquareIcon size='13' />,
  },
  {
    tooltip: 'Separate X & Y',
    value: 'axis',
    icon: <RatioIcon size='13' />,
  },
  {
    tooltip: 'Custom per side',
    value: 'individual',
    icon: <SquareSquare size='13' />,
  },
]

export {
  PREDEFINED_OPTIONS_TOGGLE,
  CUSTOM_OPTIONS_TOGGLE,
  PREDEFINED_SIZES,
  PREFIXES,
  SPACING_OPTIONS,
  parseSizeToTailwind,
  parseSpaceToTailwind,
  parseTailwindToSize,
  parseSpaceString,
}
